import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';

import { Button } from '../../UI';
import { ListMutedUsersResponse, MutedUser, UnmuteUserResponse } from './types';
import { LIST_MUTED_USERS, UNMUTE_USER } from '../../../http/apollo-queries';

interface UnmuteUserModalPropTypes {
  onClick: (e: any) => void;
  user: MutedUser;
}

const UnmuteUserModal: React.FC<UnmuteUserModalPropTypes> = ({
  onClick,
  user,
}) => {
  const { t } = useTranslation();

  return (
    <div className="p-12 px-36 text-center">
      <h2 className="font-bold text-center text-lg">
        {t('chatroom.unmuteUserModal.title', { name: user.name })}
      </h2>
      <p className="text-grey-50 pt-4">{t('chatroom.unmuteUserModal.desc')}</p>
      <div className="flex justify-center pt-8">
        <Button type="button" variant="primary" onClick={onClick}>
          {t('chatroom.unmuteUserModal.unmute')}
        </Button>
      </div>
    </div>
  );
};

const MutedUsersModal: React.FC = () => {
  const { t } = useTranslation();
  const [stage, setStage] = useState<{
    stage: number;
    user?: MutedUser;
  }>({ stage: 1 });
  const [mutedUsers, setMutedUsers] = useState<MutedUser[]>([]);

  const [listMutedUsers] = useLazyQuery<ListMutedUsersResponse>(
    LIST_MUTED_USERS,
    {
      onCompleted: (data) => {
        if (data.listMutedUsers) {
          setMutedUsers(data.listMutedUsers);
        }
      },
    }
  );
  const [unmuteUser] = useMutation<UnmuteUserResponse>(UNMUTE_USER, {
    variables: {
      userId: stage.user?.owner,
    },
    onCompleted: (data) => {
      if (data.unmuteChatUser.status === 'SUCCESS') {
        listMutedUsers().then(() =>
          setStage({
            stage: 1,
          })
        );
      }
    },
  });

  useEffect(() => {
    listMutedUsers();
  }, []);

  const handleOnClickStage1 = (user: MutedUser) => {
    setStage({
      stage: 2,
      user,
    });
  };

  const handleOnClickStage2 = unmuteUser;

  return stage.stage === 1 ? (
    <div className="p-12 px-36 m-auto text-center">
      <h2 className="font-bold text-center text-lg">
        {t('chatroom.mutedUsersModal.title')}
      </h2>
      <p className="text-grey-50 pt-4">
        {t(
          mutedUsers.length
            ? 'chatroom.mutedUsersModal.desc'
            : 'chatroom.mutedUsersModal.absentDesc'
        )}
      </p>
      {mutedUsers.map(({ name, owner }) => (
        <div
          key={owner}
          className="flex justify-between py-4 border-b-1 border-grey-10"
        >
          <span className="pr-96">{name}</span>
          <Button
            type="button"
            variant="primary"
            onClick={() => handleOnClickStage1({ name, owner })}
          >
            {t('chatroom.mutedUsersModal.unmute')}
          </Button>
        </div>
      ))}
    </div>
  ) : (
    <UnmuteUserModal
      user={stage.user as MutedUser}
      onClick={handleOnClickStage2}
    />
  );
};

export default MutedUsersModal;
